<template>
    <v-dialog v-model="existingClientDialog" max-width="720" max-height="80vh">
        <v-card class="duplicate-dialog">
            <v-toolbar class="duplicate-dialog-toolbar" >
                <template #prepend>
                    <v-icon color="warning">mdi-alert-circle-outline</v-icon>
                </template>
                <v-toolbar-title>Client posibil duplicat</v-toolbar-title>
            </v-toolbar>
            <v-card-text>
                <div class="duplicate-match-reason">
                    Pentru
                    <v-chip
                        v-for="(reason, index) in duplicateDialogData.matchReasons"
                        :key="`${reason}-${index}`"
                        size="small"
                        class="mr-1 mb-1"
                        color="primary"
                        variant="tonal"
                    >
                        {{ reason }}
                    </v-chip>,
                    s-a găsit deja cel puțin un client.
                </div>

                <v-expansion-panels
                    v-model="expandedDuplicatePanels"
                    multiple
                    class="mt-3 duplicate-panels"
                   
                    
                >
                    <v-expansion-panel
                        v-if="duplicateDialogData.myClients.length"
                        value="myClients"
                         density="compact"
                    >
                        <v-expansion-panel-title>
                            <div class="duplicate-group-title">
                                <v-icon size="15" class="mr-1">mdi-account-check-outline</v-icon>
                                Clienți din lista ta ({{ duplicateDialogData.myClients.length }})
                            </div>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <ul class="duplicate-clients-list">
                                <li
                                    v-for="client in duplicateDialogData.myClients"
                                    :key="`my-${client.id}`"
                                >
                                    <div class="duplicate-client-header">
                                        <div class="duplicate-client-name">{{ client.name }}</div>
                                        <div
                                            v-if="client.exactMatches.length"
                                            class="duplicate-exact-matches"
                                        >
                                            <v-chip
                                                size="x-small"
                                                color="success"
                                                variant="tonal"
                                            >
                                                {{ formatExactMatchChipLabel(client.exactMatches) }}
                                            </v-chip>
                                        </div>
                                    </div>
                                    <div
                                        v-if="client.county"
                                        class="duplicate-client-meta"
                                    >
                                        Județ: {{ client.county }}
                                    </div>
                                </li>
                            </ul>
                        </v-expansion-panel-text>
                    </v-expansion-panel>

                    <v-expansion-panel
                        v-if="duplicateDialogData.otherClients.length"
                        value="otherClients"                  
                         density="compact"
                    >
                        <v-expansion-panel-title>
                            <div class="duplicate-group-title">
                                <v-icon size="15" class="mr-1">mdi-account-multiple-outline</v-icon>
                                Clienți din alte liste ({{ duplicateDialogData.otherClients.length }})
                            </div>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <ul class="duplicate-clients-list">
                                <li
                                    v-for="client in duplicateDialogData.otherClients"
                                    :key="`other-${client.id}`"
                                >
                                    <div class="duplicate-client-header">
                                        <div class="duplicate-client-name">{{ client.name }}</div>
                                        <div
                                            v-if="client.exactMatches.length"
                                            class="duplicate-exact-matches"
                                        >
                                            <v-chip
                                                size="x-small"
                                                color="success"
                                                variant="tonal"
                                            >
                                                {{ formatExactMatchChipLabel(client.exactMatches) }}
                                            </v-chip>
                                        </div>
                                    </div>
                                    <div class="duplicate-client-meta">
                                        Agent: {{ client.agentName || "Necunoscut" }}
                                        <span v-if="client.county"> • Județ: {{ client.county }}</span>
                                    </div>
                                </li>
                            </ul>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>

            </v-card-text>
            <v-card-actions class="duplicate-dialog-actions">
                <div class="duplicate-dialog-actions-content">
                    <div class="duplicate-dialog-confirmation-text">
                        Ești sigur că vrei să creezi clientul?
                    </div>
                    <div class="duplicate-dialog-buttons">
                        <v-btn
                            color="primary"
                            variant="text"
                            @click="existingClientDialog = false"
                        >
                            NU
                        </v-btn>
                        <v-btn
                            color="danger"
                            variant="text"
                            data-cy="force-create-client"
                            @click="triggerForceCreateClient"
                        >
                            DA
                        </v-btn>
                    </div>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>
    <v-form ref="form" v-model="valid">
        <v-card>
            <v-card-title data-cy="client-title">
                Informații Client</v-card-title
            >
            <v-card-text>
                <v-row>
                    <v-col cols="12" md="6" sm="12">
                        <v-text-field
                            :variant="this.$store.state.vuetifyFieldType"
                            label="Nume client"
                            v-model="formData.client.full_name"
                            :rules="nameRules"
                            required
                            density="compact"
                            data-cy="client-name"
                        >
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" sm="12">
                        <v-text-field
                            clearable
                            label="Număr telefon"
                            :variant="this.$store.state.vuetifyFieldType"
                            v-model="formData.client.phone"
                            :rules="phoneRules"
                            required
                            density="compact"
                            data-cy="client-phone"
                        >
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" sm="12">
                        <v-text-field
                            id="email"
                            clearable
                            label="Adresă email"
                            :variant="this.$store.state.vuetifyFieldType"
                            v-model="formData.client.email"
                            :rules="emailRules"
                            required
                            density="compact"
                            data-cy="client-email"
                            placeholder="popescu@mail.com"
                        >
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" sm="12">
                        <v-select
                            data-cy="client-type"
                            clearable
                            label="Tipul Clientului"
                            :items="clientTypes"
                            v-model="formData.client.client_type"
                            :variant="this.$store.state.vuetifyFieldType"
                            item-text="label"
                            item-value="value"
                            :rules="clientTypeRules"
                            density="compact"
                            required
                        >
                        </v-select>
                    </v-col>
                    <v-col cols="12" md="6" sm="12">
                        <IntermediarySelect
                            :classes="'col-12 mb-4'"
                            :current-intermediary="null"
                            :selected-agent="formData.client.selectedAgent"
                            @new-intermediary="withIntermediary = true"
                            @intermediary-changed="changeIntermediary"
                        ></IntermediarySelect>
                    </v-col>
                    <v-col cols="12" md="6" sm="12">
                        <v-autocomplete
                            v-if="
                                this.userPermissions.clientManagement
                                    .canEditClientAgent
                            "
                            prepend-inner-icon="mdi-account-switch"
                            label="Nume agent"
                            :items="availableAgents"
                            item-title="full_name"
                            :rules="agentRules"
                            required
                            item-value="id"
                            :variant="this.$store.state.vuetifyFieldType"
                            density="compact"
                            v-model="formData.client.selectedAgent"
                            @update:modelValue="setDifferentAgentSelected"
                        >
                        </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="6" sm="12">
                        <v-text-field
                            clearable
                            label="CNP"
                            :variant="this.$store.state.vuetifyFieldType"
                            v-model="formData.client.cnp"
                            :rules="cnpRules"
                            density="compact"
                            data-cy="client-cnp"
                        >
                        </v-text-field>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
        <CreateIntermediary
            v-if="withIntermediary"
            :onClientCreation="true"
            @save-intermediary="saveIntermediary"
        />
        <v-card>
            <v-card-title data-cy="location-title">
                Informații locație</v-card-title
            >
            <v-card-text>
                <v-row>
                    <v-col cols="12" md="6" sm="12">
                        <v-text-field
                            clearable
                            label="Nume locație"
                            required
                            :variant="this.$store.state.vuetifyFieldType"
                            v-model="formData.location.description"
                            :rules="nameRules"
                            density="compact"
                            data-cy="location-description"
                        >
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" sm="12" v-if="!this.userPermissions?.clientManagement?.canRequireRegionOnClientForm">
                        <v-autocomplete
                            data-cy="location-region"
                            clearable
                            label="Județ"
                            :items="regions"
                            v-model="formData.location.region_id"
                            :variant="this.$store.state.vuetifyFieldType"
                            item-title="title"
                            item-value="value"
                            :rules="regionFieldRules"
                            :required="isRegionRequired"
                            density="compact"
                            autocomplete
                        >
                        </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="6" sm="12">
                        <v-text-field
                            clearable
                            label="Localitate"
                            :variant="this.$store.state.vuetifyFieldType"
                            v-model="formData.location.city"
                            :rules="cityRules"
                            required
                            data-cy="location-city"
                            density="compact"
                        >
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" sm="12">
                        <v-text-field
                            clearable
                            label="Adresă"
                            :variant="this.$store.state.vuetifyFieldType"
                            v-model="formData.location.address"
                            :rules="addressRules"
                            required
                            data-cy="location-address"
                            density="compact"
                        >
                        </v-text-field>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <v-btn
            color="success"
            class="mr-4"
            @click="saveClientWithLocation(false)"
            data-cy="createClient"
        >
            Salvează client
        </v-btn>
    </v-form>
    <v-snackbar v-model="snackbar"
        >{{ snackbarMessage }}
        <v-btn color="pink" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
</template>

<script>
import apiClient from "@/utils/apiClient";
import getCurrentDateTime from "@/utils/currentTimeFunction";
import IntermediarySelect from "@/components/clients/IntermediarySelect.vue";
import CreateIntermediary from "@/components/clients/CreateIntermediary.vue";
import { mapGetters } from "vuex";
import { fetchData, fetchRegions, showAlertModal } from "@/utils/utils";

export default {
    name: "ClientForm",
    components: {
        IntermediarySelect,
        CreateIntermediary,
    },
    data: () => ({
        formData: {
            client: {
                full_name: "",
                phone: "",
                email: "",
                cif: "",
                notes: "",
                client_type: 1,
                selectedAgent: null,
                cnp: "",
            },
            location: {
                description: "",
                region_id: null,
                city: "",
                address: "",
            },
        },
        clientTypes: [
            { title: "Client final", value: 1 },
            { title: "Instalator", value: 2 },
            { title: "Constructor", value: 3 },
            { title: "Dezvoltator", value: 4 },
            { title: "Magazin", value: 5 },
            { title: "Agent", value: 6 },
        ],
        intermediary: "",
        newIntermediary: null,
        changedIntermediary: null,
        withIntermediary: false,
        regions: [],
        show: false,
        customerSubmitted: false,
        valid: false,
        checkbox: false,
        snackbar: false,
        snackbarMessage: "",
        clientTypeRules: [
            (value) => {
                if (value) return true;
                return "Tipul clientului este obligatoriu.";
            },
        ],
        agentRules: [
            (value) => {
                if (value) return true;
                return "Numele agentului este obligatoriu.";
            },
        ],
        nameRules: [
            (value) => {
                if (value && value.trim()) return true;
                return "Denumirea este obligatorie.";
            },
        ],
        emailRules: [
            (value) => {
                if (!value || !value.trim()) {
                    return "Adresa de email este obligatorie.";
                }
                if (/.+@.+\..+/.test(value)) return true;
                return "E-mailul trebuie să fie valid";
            },
        ],
        phoneRules: [
            (value) => {
                if (!value || !value.trim()) {
                    return "Numărul de telefon este obligatoriu.";
                }
                if (value.startsWith("+")) {
                    return (
                        value.length >= 11 ||
                        "Numărul de telefon trebuie să aibă cel puțin 12 caractere."
                    ); // Checks length for numbers starting with +
                } else {
                    return (
                        value.length >= 10 ||
                        "Numărul de telefon trebuie să aibă cel puțin 10 caractere."
                    ); // Checks length for other numbers
                }
            },
        ],
        regionRules: [
            (value) => {
                if (value) return true;
                return "Județul este obligatoriu.";
            },
        ],
        cityRules: [
            (value) => {
                if (value && value.trim()) return true;
                return "Localitatea este obligatorie.";
            },
        ],
        addressRules: [
            (value) => {
                if (value && value.trim()) return true;
                return "Adresa este obligatorie.";
            },
        ],
        cnpRules: [
            (v) =>
                !v ||
                (v && v.length === 13) ||
                "CNP-ul trebuie să aibă exact 13 caractere.", // Only check length if v has a value
            (v) =>
                !v ||
                /^[12]/.test(v) ||
                "CNP-ul trebuie să înceapă cu 1 sau 2.", // Only check format if v has a value
        ],
        existingClientDialog: false,
        existingClientAgent: null,
        existingClientMessage: "",
        duplicateDialogData: {
            matchReasons: [],
            myClients: [],
            otherClients: [],
        },
        expandedDuplicatePanels: ["myClients", "otherClients"],
        isDifferentAgentSelected: false,
    }),
    async mounted() {
        this.regions = await fetchRegions();
        this.initializeSelectedAgent();
    },
    computed: {
        ...mapGetters(["userPermissions", "userRole"]),
        isRegionRequired() {
            return !this.userPermissions?.clientManagement
                ?.canRequireRegionOnClientForm;
        },
        regionFieldRules() {
            return this.isRegionRequired ? this.regionRules : [];
        },
        /**
         * A computed property that returns the list of available users who can be assigned as agents.
         * This list is filtered based on the current user's role, superuser status, and certain group restrictions.
         *
         * - If the user has the "superAgent" role, only users whose `parent_agent` matches the current user (or the user themselves) are returned.
         * - If the user is a superuser, the current user is excluded, and certain email addresses are prioritized to appear first.
         * - Any user belonging to the "notSelectableAsAgent" group is removed from the list.
         *
         * @returns {Array<Object>} An array of user objects containing information such as id, email, and parent_agent.
         */
        availableAgents() {
            let users = JSON.parse(sessionStorage.getItem('myUsers')) || [];
            let currentUserId = JSON.parse(sessionStorage.getItem('userId'));
            let isSuperUser = JSON.parse(sessionStorage.getItem('superUser'));

            users = users.filter(user => !user.groups.includes('notSelectableAsAgent'));

            if (this.userRole === 'superAgent') {
                users = users.filter(user => user.parent_agent === currentUserId || user.id === currentUserId);
            }

            if (isSuperUser) {

                // Prioritize 'test@sistema.com.ro' and 'office@sistema.com.ro'
                const specialEmails = ["test@sistema.com.ro", "office@sistema.com.ro"];
                const specialUsers = users.filter(u => specialEmails.includes(u.email));
                const otherUsers = users.filter(u => !specialEmails.includes(u.email));

                users = [...specialUsers, ...otherUsers];
            }

            return users;
        },
    },
    methods: {
        initializeSelectedAgent() {
            this.formData.client.selectedAgent = this.userPermissions
                .clientManagement.canViewHimselfAsAgent
                ? parseInt(JSON.parse(sessionStorage.getItem("userId")))
                : null;
        },
        setDifferentAgentSelected() {
            this.isDifferentAgentSelected = true;
        },
        saveIntermediary(intermediary) {
            this.newIntermediary = intermediary;
        },
        changeIntermediary(intermediary) {
            this.withIntermediary = false;
            this.changedIntermediary = intermediary;
        },
        /**
         * Asynchronously sets a special technical user as the selected agent for a client under specific conditions.
         * This method checks the current user's email stored in sessionStorage. If the email matches one of two predefined
         * email addresses, it then filters the list of users also stored in sessionStorage to find a technical user
         * with an email that includes 'tehnic@sistema.com.ro'. If such a user is found, their ID is set as the selected
         * agent for the client in the form data. This is a special case operation intended for creating clients with
         * a specific technical user agent when the current user is one of two special agents.
         *
         * Assumes 'myUsers' in sessionStorage is a JSON string representing an array of user objects, and that
         * 'userEmail' in sessionStorage is a string representing the current user's email.
         *
         * @async
         * @function setSpecialUserForTechnicalUsers
         * @throws {SyntaxError} Throws a syntax error if 'myUsers' is not valid JSON.
         * @sideEffect Modifies `this.formData.client.selectedAgent` by setting it to the ID of the found technical user.
         *             May log to the console if no technical user is found.
         */
        async setSpecialUserForTechnicalUsers() {
            let usersArray = await JSON.parse(
                sessionStorage.getItem("myUsers")
            );
            if (
                !this.isDifferentAgentSelected &&
                (sessionStorage.getItem("userEmail") ===
                    "ofertare@sistema.com.ro" ||
                    sessionStorage.getItem("userEmail") ===
                        "ofertare2@sistema.com.ro")
            ) {
                let technicalUser = usersArray.filter((user) =>
                    user.email.includes("office@sistema.com.ro")
                );
                if (technicalUser.length > 0) {
                    // Ensure at least one user was found
                    this.formData.client.selectedAgent = technicalUser[0].id;
                } else {
                    console.log("No technical user found");
                }
            }
        },
        /**
         * Triggers the creation of a client with the forceCreateClient flag set to true.
         * This method is intended to be called when a user decides to force the creation
         * of a client despite the system detecting a potential duplicate.
         */
        async triggerForceCreateClient() {
            let forceCreateClient = true;
            await this.saveClientWithLocation(forceCreateClient);
        },
        formatCountyName(regionName) {
            if (!regionName) return "";
            return regionName.replace(/\s+County$/i, "").trim();
        },
        getClientAgentId(client) {
            const rawId = client?.agent_id ?? client?.agent ?? client?.agentId;
            const parsedId = Number(rawId);
            return Number.isNaN(parsedId) ? null : parsedId;
        },
        normalizeNameForMatch(name) {
            if (!name) return "";
            return name
                .toLowerCase()
                .trim()
                .replace(/\s+/g, " ");
        },
        normalizeEmailForMatch(email) {
            return (email || "").toLowerCase().trim();
        },
        normalizePhoneForMatch(phone) {
            return (phone || "").replace(/\D/g, "");
        },
        getNameMatchScore(searchName, candidateName) {
            const normalizedSearch = this.normalizeNameForMatch(searchName);
            const normalizedCandidate = this.normalizeNameForMatch(candidateName);

            if (!normalizedSearch || !normalizedCandidate) {
                return 4;
            }

            if (normalizedCandidate === normalizedSearch) {
                return 0;
            }

            const searchTokens = normalizedSearch.split(" ");
            const candidateTokens = normalizedCandidate.split(" ");
            const reversedSearch = [...searchTokens].reverse().join(" ");

            if (normalizedCandidate === reversedSearch) {
                return 1;
            }

            const allSearchTokensPresent = searchTokens.every((token) =>
                candidateTokens.includes(token)
            );
            if (allSearchTokensPresent) {
                return 2;
            }

            if (normalizedCandidate.includes(normalizedSearch)) {
                return 3;
            }

            return 4;
        },
        getSelectedRegionName(regionId) {
            if (!regionId) return "";

            const selectedRegion = this.regions.find(
                (region) => region.value === regionId
            );
            return this.formatCountyName(selectedRegion?.title || "");
        },
        isRegionMatch(clientCounty, selectedRegionName) {
            if (!clientCounty || !selectedRegionName) {
                return false;
            }

            return (
                this.normalizeNameForMatch(clientCounty) ===
                this.normalizeNameForMatch(selectedRegionName)
            );
        },
        getExactMatchFields(client, searchClient) {
            const exactMatches = [];

            const normalizedSearchName = this.normalizeNameForMatch(
                searchClient.full_name
            );
            const normalizedClientName = this.normalizeNameForMatch(client.name);
            if (
                normalizedSearchName &&
                normalizedClientName &&
                normalizedSearchName === normalizedClientName
            ) {
                exactMatches.push("full_name");
            }

            const normalizedSearchPhone = this.normalizePhoneForMatch(
                searchClient.phone
            );
            const normalizedClientPhone = this.normalizePhoneForMatch(client.phone);
            if (
                normalizedSearchPhone &&
                normalizedClientPhone &&
                normalizedSearchPhone === normalizedClientPhone
            ) {
                exactMatches.push("phone");
            }

            const normalizedSearchEmail = this.normalizeEmailForMatch(
                searchClient.email
            );
            const normalizedClientEmail = this.normalizeEmailForMatch(client.email);
            if (
                normalizedSearchEmail &&
                normalizedClientEmail &&
                normalizedSearchEmail === normalizedClientEmail
            ) {
                exactMatches.push("email");
            }

            return exactMatches;
        },
        getClientSortScore(client, searchName) {
            const hasExactPhone = client.exactMatches.includes("phone");
            const hasExactEmail = client.exactMatches.includes("email");

            // Exact phone/email matches must be first.
            if (hasExactPhone && hasExactEmail) {
                return -2;
            }
            if (hasExactPhone || hasExactEmail) {
                return -1;
            }

            return this.getNameMatchScore(searchName, client.name);
        },
        sortClientsByNameMatch(clients, searchName, selectedRegionName = "") {
            return [...clients].sort((a, b) => {
                const scoreA = this.getClientSortScore(a, searchName);
                const scoreB = this.getClientSortScore(b, searchName);

                if (scoreA !== scoreB) {
                    return scoreA - scoreB;
                }

                // Inside top priority buckets (e.g. phone+email exact),
                // prefer stronger name matches: exact, reversed, token, partial.
                const nameScoreA = this.getNameMatchScore(searchName, a.name);
                const nameScoreB = this.getNameMatchScore(searchName, b.name);
                if (nameScoreA !== nameScoreB) {
                    return nameScoreA - nameScoreB;
                }

                if (scoreA >= 3 && selectedRegionName) {
                    const regionMatchA = this.isRegionMatch(
                        a.county,
                        selectedRegionName
                    );
                    const regionMatchB = this.isRegionMatch(
                        b.county,
                        selectedRegionName
                    );

                    if (regionMatchA !== regionMatchB) {
                        return regionMatchA ? -1 : 1;
                    }
                }

                return a.name.localeCompare(b.name, "ro", { sensitivity: "base" });
            });
        },
        mapMatchedFieldLabel(field) {
            const labels = {
                full_name: "numele",
                full_name_partial: "numele",
                phone: "numărul de telefon",
                email: "email-ul",
            };
            return labels[field] || field;
        },
        formatExactMatchChipLabel(exactMatches = []) {
            if (!exactMatches.length) {
                return "";
            }
            const labels = exactMatches.map((field) => this.mapMatchedFieldLabel(field));
            return `Potrivire 100% ${labels.join(" + ")}`;
        },
        buildDuplicateDialogData(matchedInfo, existingClientAgent, existingClient) {
            const fields = matchedInfo?.matched_field
                ? matchedInfo.matched_field.split(", ")
                : [];
            const values = matchedInfo?.matched_value
                ? matchedInfo.matched_value.split(", ")
                : [];

            const matchReasons = fields.map((field, index) => {
                const label = this.mapMatchedFieldLabel(field);
                const value = values[index] ? `: ${values[index]}` : "";
                return `${label}${value}`;
            });

            const matchedClients = existingClient?.all_matched_clients?.length
                ? existingClient.all_matched_clients
                : existingClient
                  ? [existingClient]
                  : [];

            const loggedUserId = parseInt(
                JSON.parse(sessionStorage.getItem("userId")),
                10
            );

            const normalizeClient = (client) => ({
                id: client.id,
                name: client.full_name || "Nume necunoscut",
                phone: client.phone || "",
                email: client.email || "",
                matchedFieldsExact: Array.isArray(client.matched_fields_exact)
                    ? client.matched_fields_exact
                    : [],
                agentName: client.agent_full_name || existingClientAgent || "",
                county: this.formatCountyName(client.region_name),
                agentId: this.getClientAgentId(client),
            });

            const normalizedClients = matchedClients.map((client) => {
                const normalizedClient = normalizeClient(client);
                const exactMatches = normalizedClient.matchedFieldsExact.length
                    ? [...normalizedClient.matchedFieldsExact]
                    : this.getExactMatchFields(normalizedClient, this.formData.client);
                return {
                    ...normalizedClient,
                    exactMatches,
                };
            });
            const searchName = this.formData.client.full_name;
            const selectedRegionName = this.getSelectedRegionName(
                this.formData.location.region_id
            );
            const myClients = normalizedClients.filter(
                (client) => client.agentId === loggedUserId
            );
            const otherClients = normalizedClients.filter(
                (client) => client.agentId !== loggedUserId
            );

            return {
                matchReasons,
                myClients: this.sortClientsByNameMatch(
                    myClients,
                    searchName,
                    selectedRegionName
                ),
                otherClients: this.sortClientsByNameMatch(
                    otherClients,
                    searchName,
                    selectedRegionName
                ),
            };
        },
        /**
         * Attempts to save a new client along with its location to the system. It performs validation
         * and then sends a request to the backend. If a client already exists, it handles the response
         * accordingly by displaying a message.
         *
         * @param {Boolean} [forceCreateClient=false] - Flag indicating whether to force the creation of a client, bypassing duplicate checks.
         */
        async saveClientWithLocation(forceCreateClient = false) {
            await this.setSpecialUserForTechnicalUsers();
            let validation = await this.$refs.form.validate();
            if (validation.valid) {
                if (this.newIntermediary) {
                    this.formData.client.intermediate = this.newIntermediary;
                } else if (this.changedIntermediary) {
                    this.formData.client.intermediate_id =
                        this.changedIntermediary;
                } else {
                    this.formData.client.intermediate_id = "";
                }
                this.formData.client.created = getCurrentDateTime();
                this.formData.client.modified = getCurrentDateTime();
                this.formData.client.created_by = JSON.parse(
                    sessionStorage.getItem("userId")
                );
                const requestData = {
                    client: this.formData.client,
                    location: this.formData.location,
                    forceCreateClient: forceCreateClient,
                };

                await apiClient
                    .post("/api/add_client_with_location/", requestData)
                    .then(async (response) => {
                        if (
                            response?.data?.client_exists &&
                            response?.data?.message === "Client already exists"
                        ) {
                            const matchedInfo = response.data.matched_info;
                            const existingClient = response.data.existing_client;
                            const existingClientAgent =
                                existingClient.agent_full_name;
                            this.duplicateDialogData =
                                this.buildDuplicateDialogData(
                                    matchedInfo,
                                    existingClientAgent,
                                    existingClient
                                );
                            this.expandedDuplicatePanels = [
                                ...(this.duplicateDialogData.myClients.length
                                    ? ["myClients"]
                                    : []),
                                ...(this.duplicateDialogData.otherClients.length
                                    ? ["otherClients"]
                                    : []),
                            ];
                            this.existingClientDialog = true;
                            return;
                        }
                        response.data.client.agent_id =
                            response.data.client.agent;
                        this.snackbarMessage =
                            "Clientul a fost adăugat cu succes!";
                        this.snackbar = true;
                        await fetchData("all", this);
                        this.existingClientDialog = false;
                        setTimeout(() => {
                            this.$router.push(
                                "/client-details/" +
                                    response.data.client.id +
                                    "/"
                            );
                        }, 300);
                    })
                    .catch((error) => {
                        const status = error?.response?.status;
                        const responseData = error?.response?.data;

                        if (
                            status === 400 &&
                            responseData?.message === "Client already exists"
                        ) {
                            const matchedInfo = responseData.matched_info;
                            const existingClient = responseData.existing_client;
                            const existingClientAgent = existingClient.agent_full_name;
                            this.duplicateDialogData = this.buildDuplicateDialogData(
                                matchedInfo,
                                existingClientAgent,
                                existingClient
                            );
                            this.expandedDuplicatePanels = [
                                ...(this.duplicateDialogData.myClients.length
                                    ? ["myClients"]
                                    : []),
                                ...(this.duplicateDialogData.otherClients.length
                                    ? ["otherClients"]
                                    : []),
                            ];
                            this.existingClientDialog = true;
                        } else {
                            const fallbackMessage =
                                "A apărut o eroare la adăugarea clientului.";
                            const backendDetail =
                                responseData?.details?.[0] || fallbackMessage;
                            showAlertModal(
                                this.$store,
                                "A apărut o eroare la salvarea clientului. Vă rugăm să încercați din nou.",
                                "danger",
                                12000,
                                error
                            );
                            console.error("Client create failed", {
                                status,
                                detail: backendDetail,
                            });
                            // Handle other statuses
                            this.snackbarMessage = backendDetail;
                            this.snackbar = true;
                        }
                    });
            } else {
                this.snackbarMessage = "Formularul nu este completat corect.";
                this.snackbar = true;
            }
        },
    },
};
</script>

<style scoped>
.v-form {
    margin: 20px 0 0 0;
}

.v-card {
    margin: 20px 0;
}

.duplicate-dialog {
    overflow: hidden;
}

.duplicate-dialog-toolbar {
    position: sticky;
    top: 0;
    z-index: 3;
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
    padding-left: 20px;
}



.duplicate-match-reason {
    line-height: 1.7;
    margin-top: 2px;
}

.duplicate-group-card {
    border-radius: 10px;
}

.duplicate-panels :deep(.v-expansion-panel) {
    border-radius: 10px;
    border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.duplicate-panel-mine :deep(.v-expansion-panel-title),
.duplicate-panel-mine :deep(.v-expansion-panel-text__wrapper) {
    background: rgba(var(--v-theme-success), 0.12);
}

.duplicate-panel-others :deep(.v-expansion-panel-title),
.duplicate-panel-others :deep(.v-expansion-panel-text__wrapper) {
    background: rgba(var(--v-theme-warning), 0.14);
}

.duplicate-panel-mine :deep(.duplicate-group-title),
.duplicate-panel-mine :deep(.duplicate-group-title .v-icon) {
    color: rgba(var(--v-theme-success), 0.85);
}

.duplicate-panel-others :deep(.duplicate-group-title),
.duplicate-panel-others :deep(.duplicate-group-title .v-icon) {
    color: rgba(var(--v-theme-warning), 0.9);
}

.duplicate-group-title {
    font-weight: 500;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    font-size: 0.97rem;
}

.duplicate-clients-list {
    margin: 0;
    padding-left: 20px;
}

.duplicate-clients-list li {
    margin-bottom: 6px;
}

.duplicate-client-name {
    font-weight: 500;
    font-size: 0.95rem;
}

.duplicate-client-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.duplicate-client-meta {
    opacity: 0.85;
    font-size: 0.83rem;
}

.duplicate-exact-matches {
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
    justify-content: flex-end;
    flex-shrink: 0;
}

.duplicate-exact-matches :deep(.v-chip) {
    max-width: 100%;
}

.duplicate-exact-matches :deep(.v-chip__content) {
    white-space: nowrap;
}

@media (max-width: 600px) {
    .duplicate-client-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
    }

    .duplicate-exact-matches {
        width: 100%;
        justify-content: flex-start;
    }

    .duplicate-exact-matches :deep(.v-chip__content) {
        white-space: normal;
        word-break: break-word;
        line-height: 1.2;
    }
}

.duplicate-dialog-actions {
    position: sticky;
    bottom: 0;
    z-index: 2;
    background: rgb(var(--v-theme-surface));
    border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
    padding: 12px 16px;
}

.duplicate-dialog-actions-content {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.duplicate-dialog-confirmation-text {
    font-weight: 500;
}

.duplicate-dialog-buttons {
    display: flex;
    align-items: center;
    gap: 8px;
}
</style>
